/**
 * Color utilities with OKLab interpolation for perceptually uniform gradients.
 * OKLab conversion follows Björn Ottosson's reference (https://bottosson.github.io/posts/oklab/).
 */

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

const srgbChannelToLinear = (channel: number): number => {
  const c = channel / 255
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

const linearToSrgbChannel = (channel: number): number => {
  const c = channel <= 0.0031308 ? 12.92 * channel : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055
  return Math.round(clamp01(c) * 255)
}

export type RgbColor = { r: number; g: number; b: number }
export type OklabColor = { L: number; a: number; b: number }

export const parseHexColor = (hex: string): RgbColor | null => {
  const raw = hex.trim().replace(/^#/, '')
  if (![3, 6].includes(raw.length) || !/^[0-9a-fA-F]+$/.test(raw)) {
    return null
  }
  const normalized =
    raw.length === 3 ? `${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}` : raw
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  }
}

export const rgbToOklab = ({ r, g, b }: RgbColor): OklabColor => {
  const lr = srgbChannelToLinear(r)
  const lg = srgbChannelToLinear(g)
  const lb = srgbChannelToLinear(b)

  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb)
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb)
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb)

  return {
    L: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  }
}

export const oklabToRgb = ({ L, a, b }: OklabColor): RgbColor => {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b
  const s_ = L - 0.0894841775 * a - 1.291485548 * b

  const l = l_ * l_ * l_
  const m = m_ * m_ * m_
  const s = s_ * s_ * s_

  return {
    r: linearToSrgbChannel(+4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: linearToSrgbChannel(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: linearToSrgbChannel(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  }
}

export const rgbToCss = ({ r, g, b }: RgbColor): string => `rgb(${r}, ${g}, ${b})`

/**
 * Interpolate two hex colors in OKLab for a more perceptually uniform blend.
 * `t` is clamped to [0, 1]. Invalid hex inputs fall back to the start color.
 */
export const interpolateHexColorsOklab = (startHex: string, endHex: string, t: number): string => {
  const startRgb = parseHexColor(startHex)
  const endRgb = parseHexColor(endHex)
  if (!startRgb || !endRgb) {
    return startHex
  }

  const safeT = clamp01(t)
  const start = rgbToOklab(startRgb)
  const end = rgbToOklab(endRgb)
  const blended = oklabToRgb({
    L: start.L + (end.L - start.L) * safeT,
    a: start.a + (end.a - start.a) * safeT,
    b: start.b + (end.b - start.b) * safeT,
  })
  return rgbToCss(blended)
}
